import { Dictionary } from 'lodash';

import { featureDetection } from '../ui/libs/featureDetection';

export class Layout {

  static MOBILE_WIDTH = 767;
  static TABLET_WIDTH = 970;
  static TABLET_BIG_WIDTH = 1023;
  static NOTEBOOK_WIDTH = 1440;

  isMobile = false;
  isTablet = false;
  isNotebook = false;
  isDesktop = false;

  documentDOMContentLoadedListeners = [];

  private listeners = [];
  private documentClickListeners: Dictionary<(e: Event) => void> = {};
  private windowResizeEventListeners: Dictionary<() => void> = {};

  constructor() {
    this.init();
  }

  isMobileLayout(): boolean {
    return window.innerWidth <= Layout.MOBILE_WIDTH;
  }

  isTabletLayout(): boolean {
    return window.innerWidth <= Layout.TABLET_WIDTH && !this.isMobileLayout();
  }

  isTabletBigLayout(): boolean {
    return window.innerWidth <= Layout.TABLET_BIG_WIDTH && !this.isTabletLayout() && !this.isMobileLayout();
  }

  isNotebookLayout(): boolean {
    return window.innerWidth <= Layout.NOTEBOOK_WIDTH && !this.isTabletBigLayout() && !this.isTabletLayout() && !this.isMobileLayout();
  }

  isDesktopLayout(): boolean {
    return !this.isNotebookLayout() && !this.isTabletBigLayout() && !this.isTabletLayout() && !this.isMobileLayout();
  }

  // will be true if "isDesktopLayout" or "isNotebookLayout" will be true. In other cases will be false.
  isDesktopDevice(): boolean {
    return this.isDesktopLayout() || this.isNotebookLayout();
  }

  addListener(func): void {
    this.listeners.push(func);
  }

  fireChangeMode(): void {
    setTimeout(() => {
      for (const listener of this.listeners) {
        listener(this.isMobile);
      }
    });
  }

  addDocumentClickHandler(key: string, handler): void {
    if (key && typeof handler === 'function') {
      this.documentClickListeners[key] = handler;
    }
  }

  removeDocumentClickHandler(key: string): void {
    delete(this.documentClickListeners[key]);
  }

  fireDocumentClick(e): void {
    Object.values(this.documentClickListeners)
      .forEach(handler => {
        handler(e);
      });
  }

  addWindowResizeHandler(key: string, handler): void {
    if (key && typeof handler === 'function') {
      this.windowResizeEventListeners[key] = handler;
    }
  }

  removeWindowResizeHandler(key: string): void {
    delete(this.windowResizeEventListeners[key]);
  }

  fireWindowResize(): void {
    Object.values(this.windowResizeEventListeners)
      .forEach(handler => {
        handler();
      });
  }

  fireDOMContentLoaded(): void {
    this.documentDOMContentLoadedListeners.forEach(handler => handler());
  }

  isTouchDevice(): boolean {
    return 'ontouchstart' in document.documentElement;
  }

  init(): void {
    this.bindEvents();
    this.initDevices();
    this.fireChangeMode();
  }

  initDevices(): void {
    this.isMobile = this.isMobileLayout();
    this.isTablet = this.isTabletLayout();
    this.isNotebook = this.isNotebookLayout();
    this.isDesktop = this.isDesktopLayout();
  }

  bindEvents(): void {
    window.addEventListener('resize', () => {
      this.initDevices();
      this.fireChangeMode();
      this.fireWindowResize();
    });

    let documentClick = false;
    const handleTouchend = ev => {
      if (ev.type === 'click') {
        documentClick = true;
      }
      if (documentClick) {
        this.fireDocumentClick(ev);
      }
    };

    const listenerOpt = featureDetection.supportsPassive ? {passive: true} : false;

    document.addEventListener('touchstart', () => {
      documentClick = true;
    }, listenerOpt);
    document.addEventListener('touchmove', () => {
      documentClick = false;
    }, listenerOpt);
    document.addEventListener('click', handleTouchend.bind(this));
    document.addEventListener('touchend', handleTouchend.bind(this), listenerOpt);
    document.addEventListener('DOMContentLoaded', () => this.fireDOMContentLoaded());
  }

}

export const layout = new Layout();
